import { Ear, HandHeart, Leaf, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, ParallaxImage, Reveal } from '../components/Reveal';

const VALUES = [
  {
    icon: Ear,
    title: 'Listening first',
    text: 'Every session starts with an unhurried intake. Your history and goals shape the treatment — not a menu.',
  },
  {
    icon: ShieldCheck,
    title: 'Clinical grounding',
    text: 'Medically-informed technique, careful documentation, and coordination with your care team when it matters.',
  },
  {
    icon: HandHeart,
    title: 'Warmth always',
    text: 'Doula-trained presence: the room is calm, the pace is gentle, and you are never rushed or processed.',
  },
  {
    icon: Leaf,
    title: 'Continuity of care',
    text: 'We remember what worked last time and build on it. Massage as ongoing healthcare, not a one-off treat.',
  },
];

const STORY = [
  'With a professional background rooted in healthcare, medical insurance compliance, maternal support, and therapeutic wellness, Mimi has built her practice around one central belief: care should be both intentional and personal.',
  'Her journey into massage therapy and doula work grew from a natural desire to support people through some of the most vulnerable and transformative moments of their lives. Over time, that passion evolved into a wellness practice focused on helping clients feel heard, supported, and cared for throughout every stage of their wellness journey.',
  'Through Thula Doula Wellness, Mimi provides and leads services including medical massage, therapeutic massage, prenatal massage, postpartum support, and other wellness-focused treatments. Her approach combines clinical awareness with compassionate, individualized care, allowing each client’s treatment experience to reflect their specific needs, goals, and comfort level.',
  'Mimi is especially passionate about creating an environment where clients feel safe, respected, and empowered to take an active role in their health and well-being. Whether someone is navigating chronic discomfort, recovering physically, preparing for motherhood, or simply seeking intentional therapeutic care, her goal is to provide a space where healing and restoration feel supported.',
  'As Thula Doula Wellness continues to grow, Mimi’s vision extends beyond providing massage services. She is building a practice centered around education, collaboration, high-quality care, and a team of professionals who share the same commitment to serving clients with compassion and purpose.',
];

export default function About() {
  return (
    <>
      <SEO
        title="About | Thula Doula Wellness — Humble, TX"
        description="Meet Mignon “Mimi” Fields — Licensed Massage Therapist, Certified Birth Doula, and founder of Thula Doula Wellness in Humble, Texas. Care that is intentional and personal."
      />
      <PageHeader
        testId="about-page-header"
        eyebrow="About Us"
        lines={['A quiet practice,', 'built on listening.']}
        sub="Thula Doula Wellness blends clinical, medically-informed bodywork with the warmth of doula care."
      />

      <section className="bg-brand-cream py-16 md:py-24" data-testid="about-founder">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-teal">
                <span className="h-px w-10 bg-brand-teal/40" aria-hidden="true" />
                Meet the founder
              </p>
            </Reveal>
            <MaskedLinesOnView
              as="h2"
              lines={[
                'Mignon “Mimi”',
                <span key="mimi" className="italic text-brand-teal">Fields.</span>,
              ]}
              className="mt-8 font-display text-[clamp(2.5rem,5vw,4.25rem)] font-light leading-[1.02] tracking-tight text-brand-ink"
            />
            <Reveal delay={0.2}>
              <ul className="mt-7 flex flex-wrap gap-2.5" data-testid="founder-credentials">
                {['Licensed Massage Therapist', 'Certified Birth Doula', 'Founder, Thula Doula Wellness'].map(
                  (cred) => (
                    <li
                      key={cred}
                      className="rounded-full bg-brand-sage/40 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-teal"
                    >
                      {cred}
                    </li>
                  )
                )}
              </ul>
            </Reveal>
            <Reveal delay={0.3}>
              <ParallaxImage
                src="/mimi.jpg"
                alt="Mignon “Mimi” Fields, founder of Thula Doula Wellness"
                className="mt-10 aspect-[4/3] w-full rounded-3xl"
                strength={26}
              />
              <p className="mt-4 text-xs uppercase tracking-[0.25em] text-brand-moss/70">
                Mignon “Mimi” Fields — Founder
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pt-14">
            <div className="max-w-2xl space-y-6">
              {STORY.map((para, i) => (
                <Reveal key={i} delay={0.08 + i * 0.06}>
                  <p className="text-base leading-relaxed text-brand-moss md:text-lg">{para}</p>
                </Reveal>
              ))}
              <Reveal delay={0.4}>
                <p
                  className="border-l-2 border-brand-sage pl-6 text-sm italic leading-relaxed text-brand-moss/90"
                  data-testid="founder-personal-note"
                >
                  When she is away from the business, Mimi enjoys slowing down with a good book,
                  discovering a new television series, and finding moments to recharge and
                  reconnect.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-parchment py-20 md:py-28" data-testid="about-quote">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <MaskedLinesOnView
            as="blockquote"
            lines={[
              '“My purpose has always been to guide and care for others, leading them down a path where they can advocate for, care for, and love themselves.',
              'I want to provide a space where people feel safe, seen, supported, and comforted.”',
            ]}
            className="font-display text-[clamp(1.7rem,3.4vw,3rem)] font-light italic leading-[1.25] tracking-tight text-brand-ink"
          />
          <Reveal delay={0.4}>
            <p className="mt-8 flex items-center gap-4 text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">
              <span className="h-px w-10 bg-brand-teal/40" aria-hidden="true" />
              Mignon “Mimi” Fields — Founder
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={['What we stand on.']}
            className="font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-3xl bg-brand-parchment p-8 transition-transform duration-500 hover:-translate-y-1">
                  <v.icon className="size-7 text-brand-teal" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display text-2xl font-light tracking-tight text-brand-ink">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-moss">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-20 md:pb-28" data-testid="about-credentials">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="flex flex-col items-start justify-between gap-10 border-y border-brand-ink/10 py-14 md:flex-row md:items-center">
            {[
              'Led by a Licensed Massage Therapist & Certified Birth Doula',
              'Prenatal & postpartum trained',
              'Veteran medical massage · VA Community Care welcome',
              'Licensed Massage Establishment — Texas Lic. No. ME10742',
            ].map((line) => (
              <Reveal key={line}>
                <p
                  className="max-w-[16rem] font-display text-2xl font-light leading-snug tracking-tight text-brand-ink"
                  data-testid="about-credential"
                >
                  {line}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 text-center md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={[<span key="q" className="italic">A space where every body</span>, 'can finally exhale.']}
            className="font-display text-[clamp(2.5rem,6vw,5rem)] font-light leading-[1.05] tracking-tight text-brand-ink"
          />
        </div>
      </section>
    </>
  );
}
